import React, { useState, useEffect } from "react";
import {
  Tag,
  Plus,
  X,
  Loader2,
  ChevronRight,
  Package,
  Layers,
  FileText,
  Gauge,
  Droplets,
  Thermometer,
  Zap,
  Activity,
  Cpu,
  Radio,
  Wifi,
  Cog,
  FlaskConical,
  Factory,
  HardDrive
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { toast } from "sonner";
import { v2Api } from "@/lib/v2Api";
import { Link } from "react-router-dom";

const getIconComponent = (iconName) => {
  const iconMap = {
    "gauge": Gauge, "droplets": Droplets, "thermometer": Thermometer,
    "zap": Zap, "activity": Activity, "cpu": Cpu, "radio": Radio,
    "wifi": Wifi, "cog": Cog, "flask-conical": FlaskConical,
    "factory": Factory, "hard-drive": HardDrive, "package": Package,
  };
  return iconMap[iconName] || Package;
};

// Props:
//   rfpId        — RFP UUID
//   sectionIndex — numeric section_index (from SectionOut)
//   initialTags  — SectionOut.product_tags array; no per-component fetch needed
//   sectionTitle — for toast messages
const ProductTagger = ({ rfpId, sectionIndex, initialTags = [], sectionTitle }) => {
  const [products, setProducts] = useState([]);
  const [tags, setTags] = useState(initialTags);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  // Keep tags in sync if the parent re-fetches sections (e.g. after reclassify)
  useEffect(() => { setTags(initialTags); }, [initialTags]);

  // Lazy-load the product catalogue only when the popover opens
  useEffect(() => {
    if (!open || products.length > 0) return;
    v2Api.catalog.products()
      .then((data) => setProducts(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => setProducts([]));
  }, [open]);

  const addTag = async (product, section = null, subsection = null) => {
    setLoading(true);
    try {
      const created = await v2Api.rfp.addProductTag(rfpId, sectionIndex, {
        product_id: product.id,
        product_name: product.name,
        section_id_tag: section?.id ?? null,
        section_name: section?.name ?? null,
        subsection_id: subsection?.id ?? null,
        subsection_name: subsection?.name ?? null,
      });
      setTags((prev) => [...prev, created]);
      const label = subsection
        ? `${product.name} > ${section.name} > ${subsection.name}`
        : section ? `${product.name} > ${section.name}` : product.name;
      toast.success(`Tagged with ${label}`);
      setOpen(false);
    } catch {
      toast.error("Failed to add tag");
    } finally {
      setLoading(false);
    }
  };

  const removeTag = async (tagId) => {
    try {
      await v2Api.rfp.removeProductTag(rfpId, sectionIndex, tagId);
      setTags((prev) => prev.filter((t) => t.id !== tagId));
      toast.success("Tag removed");
    } catch {
      toast.error("Failed to remove tag");
    }
  };

  const getTagLabel = (tag) => {
    if (tag.subsection_name) return `${tag.product_name} > ${tag.section_name} > ${tag.subsection_name}`;
    if (tag.section_name) return `${tag.product_name} > ${tag.section_name}`;
    return tag.product_name;
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="product-tagger">
      {tags.map((tag) => (
        <Badge
          key={tag.id}
          variant="outline"
          className="bg-purple-50 text-purple-700 border-purple-200 pr-1 flex items-center gap-1"
        >
          <Tag className="w-3 h-3" />
          <span className="max-w-[200px] truncate">{getTagLabel(tag)}</span>
          <button
            onClick={() => removeTag(tag.id)}
            className="ml-1 p-0.5 hover:bg-purple-100 rounded"
          >
            <X className="w-3 h-3" />
          </button>
        </Badge>
      ))}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs border-dashed"
            data-testid="add-product-tag-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            Tag Product
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-3 border-b border-[#E2E8F0]">
            <h4 className="font-semibold text-sm">Link to Product</h4>
            <p className="text-xs text-[#64748B]">Select product, section, or subsection</p>
          </div>
          <div className="max-h-[300px] overflow-y-auto p-2">
            {products.length === 0 ? (
              <div className="text-center py-4 text-sm text-[#64748B] space-y-1.5">
                <div>No products defined for this workspace.</div>
                <Link to="/v2/catalog" className="text-blue-600 hover:underline inline-block">
                  Open the Products catalog →
                </Link>
              </div>
            ) : (
              products.map((product) => (
                <div key={product.id} className="mb-1">
                  <div
                    className="flex items-center gap-2 p-2 hover:bg-[#F1F5F9] rounded cursor-pointer"
                    onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}
                  >
                    <div className="w-6 h-6 rounded flex items-center justify-center"
                         style={{ backgroundColor: `${product.color}20` }}>
                      {React.createElement(getIconComponent(product.icon), {
                        className: "w-3 h-3", style: { color: product.color }
                      })}
                    </div>
                    <span className="flex-1 text-sm font-medium">{product.name}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                      onClick={(e) => { e.stopPropagation(); addTag(product); }}
                      disabled={loading}>
                      <Plus className="w-3 h-3" />
                    </Button>
                    {product.sections?.length > 0 && (
                      <ChevronRight className={`w-4 h-4 text-[#64748B] transition-transform ${expandedProduct === product.id ? "rotate-90" : ""}`} />
                    )}
                  </div>

                  {expandedProduct === product.id && product.sections?.map((section) => (
                    <div key={section.id} className="ml-6">
                      <div
                        className="flex items-center gap-2 p-2 hover:bg-[#F1F5F9] rounded cursor-pointer"
                        onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                      >
                        <Layers className="w-3 h-3 text-[#64748B]" />
                        <span className="flex-1 text-sm">{section.name}</span>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                          onClick={(e) => { e.stopPropagation(); addTag(product, section); }}
                          disabled={loading}>
                          <Plus className="w-3 h-3" />
                        </Button>
                        {section.subsections?.length > 0 && (
                          <ChevronRight className={`w-4 h-4 text-[#64748B] transition-transform ${expandedSection === section.id ? "rotate-90" : ""}`} />
                        )}
                      </div>

                      {expandedSection === section.id && section.subsections?.map((subsection) => (
                        <div key={subsection.id}
                          className="flex items-center gap-2 p-2 ml-6 hover:bg-[#F1F5F9] rounded cursor-pointer"
                          onClick={() => addTag(product, section, subsection)}
                        >
                          <FileText className="w-3 h-3 text-[#94A3B8]" />
                          <span className="flex-1 text-sm text-[#64748B]">{subsection.name}</span>
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" disabled={loading}>
                            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                          </Button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ProductTagger;
